import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PendingValidationChips } from "@/components/admin/PendingValidationChips";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MoneyInput } from "@/components/ui/money-input";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import {
  LinkChangeBanner,
  LinkChangeByline,
  LinkChangeDecisionControls,
  LinkChangeDiff,
  LinkChangeNote,
  LinkChangeNoteField,
  LinkChangeReviewModal,
} from "@/components/LinkChangeReview";
import { LinkOrderDialog } from "@/components/LinkOrderDialog";
import { useServiceRegionsQuery } from "@/features/expert/api/hooks";
import { isRealisticPhone } from "@/features/expert/lib/contact";
import { SelectionField } from "@/features/expert/components/registration/steps/controls";
import { useCompanySizes, useSectors } from "@/features/questionnaire/api";
import type { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/utils/cn";
import { amountToApi, formatAmountForInput } from "@/lib/utils/money";

import {
  useActionFinancialAidOrder,
  useActionFinancialAidOrderDecision,
  useAdminFinancialAidsQuery,
  useApproveFinancialAidLinkChanges,
  useCreateFinancialAid,
  useDeleteFinancialAid,
  useFinancialAidHistory,
  useFinancialAidLinkChange,
  useFinancialAidLinkChangeDecision,
  useFinancialAidLinkChangesQuery,
  useImportFinancialAidActions,
  useImportFinancialAids,
  useTerritoryCatalog,
  useUpdateFinancialAid,
} from "../../api/hooks";
import { formatEligibility } from "../../format";
import type { FinancialAidAdmin, FundingType, PickerAction } from "../../types";
import { ActionPicker } from "./ActionPicker";
import { SORT_KEYS, sortAids, type SortKey } from "./sorting";
import { StatusPill } from "./StatusPill";
import { EmptyState, ErrorState, Loading } from "../ui/states";

const FUNDING_TYPES: FundingType[] = [
  "grant",
  "loan",
  "tax_credit",
  "guarantee",
  "other",
];

// The fixed EnterpriseProfile.EnterpriseType keys an aid can target. Labels are
// resolved via i18n (financialAids.orgTypes.*), so the slice stays self-contained.
const ORG_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

interface FormState {
  title: string;
  program_number: string;
  organization: string;
  funding_types: FundingType[];
  amount_min: string;
  amount_max: string;
  amount_description: string;
  eligibility_start: string;
  eligibility_end: string;
  // Contact person, mirroring the expert respondent fields.
  contact_last_name: string;
  contact_first_name: string;
  contact_function: string;
  contact_email: string;
  contact_phone: string;
  contact_extension: string;
  official_url: string;
  summary: string;
  is_active: boolean;
  // Relevance/targeting metadata (parity with the Django form): MRC names, region
  // + sector ids (M2M), and organization-type keys.
  territories: string[];
  service_regions: string[];
  sectors_served: string[];
  company_sizes_served: string[];
  organization_types_served: string[];
  // Free-text précisions beside each of the three catalogue selectors.
  service_regions_other: string;
  sectors_served_other: string;
  organization_types_served_other: string;
  actions: PickerAction[];
}

function toFormState(aid?: FinancialAidAdmin): FormState {
  return {
    title: aid?.title ?? "",
    program_number: aid?.program_number ?? "",
    organization: aid?.organization ?? "",
    funding_types: aid?.funding_types ?? [],
    // Grouped for display ("10 000"); normalized again in toBody.
    amount_min: formatAmountForInput(aid?.amount_min),
    amount_max: formatAmountForInput(aid?.amount_max),
    amount_description: aid?.amount_description ?? "",
    eligibility_start: aid?.eligibility_start ?? "",
    eligibility_end: aid?.eligibility_end ?? "",
    contact_last_name: aid?.contact_last_name ?? "",
    contact_first_name: aid?.contact_first_name ?? "",
    contact_function: aid?.contact_function ?? "",
    contact_email: aid?.contact_email ?? "",
    contact_phone: aid?.contact_phone ?? "",
    contact_extension: aid?.contact_extension ?? "",
    official_url: aid?.official_url ?? "",
    summary: aid?.summary ?? "",
    is_active: aid?.is_active ?? false,
    territories: aid?.territories ?? [],
    service_regions: aid?.service_regions ?? [],
    sectors_served: aid?.sectors_served ?? [],
    company_sizes_served: aid?.company_sizes_served ?? [],
    organization_types_served: aid?.organization_types_served ?? [],
    service_regions_other: aid?.service_regions_other ?? "",
    sectors_served_other: aid?.sectors_served_other ?? "",
    organization_types_served_other: aid?.organization_types_served_other ?? "",
    actions: (aid?.links ?? []).map((l) => ({
      id: l.action,
      name: l.action_name ?? l.action,
      goal: l.goal ?? "",
      issue: l.issue ?? "",
    })),
  };
}

/**
 * The write payload. ``linksFrozen`` omits ``links`` entirely: while a change
 * awaits a second administrator the picker is locked, so a save carries no link
 * edit — and sending the set anyway would have the server report a link change
 * as dropped when the administrator only corrected a title.
 */
function toBody(
  form: FormState,
  linksFrozen = false,
): Partial<FinancialAidAdmin> {
  return {
    title: form.title,
    program_number: form.program_number,
    organization: form.organization,
    funding_types: form.funding_types,
    // Decimal/date fields are nullable — an empty input clears them. The
    // amounts carry group separators, which DRF's DecimalField would reject.
    amount_min: amountToApi(form.amount_min),
    amount_max: amountToApi(form.amount_max),
    amount_description: form.amount_description,
    eligibility_start: form.eligibility_start || null,
    eligibility_end: form.eligibility_end || null,
    contact_last_name: form.contact_last_name,
    contact_first_name: form.contact_first_name,
    contact_function: form.contact_function,
    contact_email: form.contact_email,
    contact_phone: form.contact_phone,
    contact_extension: form.contact_extension,
    official_url: form.official_url,
    summary: form.summary,
    is_active: form.is_active,
    territories: form.territories,
    service_regions: form.service_regions,
    sectors_served: form.sectors_served,
    company_sizes_served: form.company_sizes_served,
    organization_types_served:
      form.organization_types_served as FinancialAidAdmin["organization_types_served"],
    service_regions_other: form.service_regions_other,
    sectors_served_other: form.sectors_served_other,
    organization_types_served_other: form.organization_types_served_other,
    // No per-aid order: the picker no longer ranks an aid's actions — ordering is
    // per action, staged behind four-eyes (see the reorder flow).
    ...(linksFrozen
      ? {}
      : { links: form.actions.map((item) => ({ action: item.id })) }),
  } as Partial<FinancialAidAdmin>;
}

/**
 * What the server actually objected to, when it said. A save can fail on a field
 * the admin didn't touch (a legacy territory, a phone that predates the phone
 * rule), and a bare "Une erreur est survenue" leaves them with no way to find it.
 */
function saveError(err: unknown, fallback: string) {
  if (typeof err !== "object" || err === null || !("status" in err)) return fallback;
  const api = err as ApiError;
  if (api.detail) return api.detail;
  const fields = Object.entries(api.fieldErrors ?? {});
  if (fields.length === 0) return fallback;
  return fields.map(([field, messages]) => `${field}: ${messages.join(" ")}`).join(" · ");
}

/** CQDD financial-aids back-office: a list with create/edit form and .xlsx import. */
export function AdminApp() {
  const { t } = useTranslation();
  const list = useAdminFinancialAidsQuery();
  const createAid = useCreateFinancialAid();
  const updateAid = useUpdateFinancialAid();
  const deleteAid = useDeleteFinancialAid();
  const importAids = useImportFinancialAids();
  const importAidActions = useImportFinancialAidActions();
  // Reference lists for the targeting selectors (shared with the directory).
  const regions = useServiceRegionsQuery();
  const sectors = useSectors();
  const companySizes = useCompanySizes();
  const territories = useTerritoryCatalog();

  // Held by id and re-read from the list query, never snapshotted: a
  // decision taken in the reorder dialog invalidates that query, and a
  // copy taken at click time would keep reporting the state the row had
  // before it (the "à valider" chip outliving its own approval).
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing =
    (editingId && (list.data ?? []).find((item) => item.id === editingId)) ||
    null;
  // The reorder dialog is opened per linked action, from the list below the
  // picker.
  const [orderAction, setOrderAction] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  // Listing order. The Django table sorts on a column-header click; a card list
  // has no headers to click, so the same columns are offered as a picker.
  const [sort, setSort] = useState<SortKey>("default");
  const [descending, setDescending] = useState(false);
  const [form, setForm] = useState<FormState>(toFormState());
  // Per-item change-history drawer (titled with the aid's name, like the
  // Django drawer), opened from the edit form — not the listing.
  const [historyItem, setHistoryItem] = useState<{ id: string; name: string } | null>(
    null,
  );
  const history = useFinancialAidHistory(historyItem?.id ?? null);

  // Link changes staged for *this* administrator to validate: the list banner
  // and its review modal, and — on the edit form — the decision on one aid.
  const linkChanges = useFinancialAidLinkChangesQuery();
  const approveLinkChanges = useApproveFinancialAidLinkChanges();
  const [reviewing, setReviewing] = useState(false);
  // "En attente de validation" only, the same switch the Django list carries.
  const [pendingOnly, setPendingOnly] = useState(false);
  const pendingChange = useFinancialAidLinkChange(editingId);
  const decideLinkChange = useFinancialAidLinkChangeDecision(editingId ?? "");
  const pending = pendingChange.data ?? null;
  const [countering, setCountering] = useState(false);
  const [counterNote, setCounterNote] = useState("");
  const [counterActions, setCounterActions] = useState<PickerAction[]>([]);
  // The proposed set, shown instead of the live one while a change is staged —
  // a reviewer decides on what was proposed, not on what is live.
  const proposedActions: PickerAction[] = (pending?.proposed ?? []).map(
    (action) => ({
      id: action.id,
      name: action.name,
      goal: action.goal,
      issue: action.issue,
    }),
  );
  // Which of them approval would *add* — marked in the locked list, as the
  // Django proposed list marks them.
  const proposedNewIds = new Set(
    (pending?.proposed ?? []).filter((a) => a.is_new).map((a) => a.id),
  );

  // Only a *saved* link can be reordered, and only where there is something to
  // order — the count and the pending state ride on the link, so the row's
  // button costs no extra request. A row the administrator just added ranks
  // nothing yet and gets no button.
  const orderByAction = new Map(
    (editing?.links ?? [])
      .filter(
        // The reorder endpoint 404s once the action's thematic stops offering
        // this kind of resource, and the links outlive that flag — so no button.
        (link) =>
          (link.action_order_allowed ?? false) &&
          ((link.action_link_count ?? 0) > 1 ||
            (link.action_order_pending ?? false)),
      )
      .map((link) => [
        link.action,
        {
          count: link.action_link_count ?? 0,
          // Two very different states: "à valider" is waiting on you, "en
          // attente" is your own proposal waiting on someone else.
          pendingReview:
            (link.action_order_pending ?? false) &&
            !(link.action_order_pending_mine ?? false),
          pendingMine: link.action_order_pending_mine ?? false,
        },
      ]),
  );

  const startCreate = () => {
    setForm(toFormState());
    setEditingId(null);
    setCreating(true);
  };
  const startEdit = (aid: FinancialAidAdmin) => {
    setForm(toFormState(aid));
    setEditingId(aid.id);
    setCreating(true);
  };
  const cancel = () => {
    setCreating(false);
    setEditingId(null);
  };

  // Blank is fine (the contact block is optional); anything typed has to be a
  // realistic number, the rule expert/registration_sync.py holds the server to.
  const phoneInvalid = !isRealisticPhone(form.contact_phone);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneInvalid) {
      toast.error(t("financialAids.admin.invalidPhone"));
      return;
    }
    try {
      const saved = editing
        ? await updateAid.mutateAsync({
            id: editing.id,
            body: toBody(form, Boolean(pending)),
          })
        : await createAid.mutateAsync(toBody(form));
      toast.success(
        t(editing ? "financialAids.admin.updated" : "financialAids.admin.created"),
      );
      reportLinkOutcome(saved);
      cancel();
    } catch (err) {
      toast.error(saveError(err, t("financialAids.error")));
    }
  };

  const remove = async (aid: FinancialAidAdmin) => {
    if (!window.confirm(t("financialAids.admin.confirmDelete"))) return;
    try {
      await deleteAid.mutateAsync(aid.id);
      toast.success(t("financialAids.admin.deleted"));
    } catch {
      toast.error(t("financialAids.error"));
    }
  };

  const onImport = async (file: File) => {
    try {
      const report = await importAids.mutateAsync(file);
      toast.success(
        t("financialAids.admin.imported", {
          created: report.created,
          updated: report.updated,
          skipped: report.skipped,
        }),
      );
      reportCancelledReviews(report);
    } catch {
      toast.error(t("financialAids.error"));
    }
  };

  const onImportActions = async (file: File) => {
    try {
      const report = await importAidActions.mutateAsync(file);
      toast.success(
        t("financialAids.admin.actionsImported", {
          pairs: report.pairs_applied,
          aids: report.aids_matched,
        }),
      );
      reportCancelledReviews(report);
    } catch {
      toast.error(t("financialAids.error"));
    }
  };

  // A save never applies the links: it stages them, and while a change is
  // already pending it stages nothing at all. Either way the administrator has
  // to be told — a silent success would read as "the links are live now".
  const reportLinkOutcome = (saved: unknown) => {
    const outcome = (saved as { link_change_outcome?: string | null } | null)
      ?.link_change_outcome;
    if (outcome === "staged") toast.info(t("financialAids.linkChange.staged"));
    if (outcome === "frozen") toast.warning(t("financialAids.linkChange.frozen"));
  };

  const startCounter = () => {
    // A counter-proposal starts from what was proposed, not from the live set.
    setCounterActions(proposedActions);
    setCounterNote("");
    setCountering(true);
  };

  const decide = async (
    decision: Parameters<typeof decideLinkChange.mutateAsync>[0],
  ) => {
    try {
      await decideLinkChange.mutateAsync(decision);
      setCountering(false);
      setCounterNote("");
      toast.success(
        t(
          decision.decision === "approve"
            ? "financialAids.linkChange.approved"
            : "financialAids.linkChange.countered",
        ),
      );
    } catch (e) {
      // ``unwrap`` throws the normalized ApiError shape — the service's message
      // (already localized) lands in ``detail``, and ``code`` says how to read
      // it. ``noop`` means the reviewer asked for nothing, which is information
      // rather than a failure: the same split ``_report`` makes in Django.
      const error = e as { detail?: string; code?: string } | null;
      const report = error?.code === "noop" ? toast.info : toast.error;
      report(error?.detail || t("financialAids.error"));
    }
  };

  const approveAll = async (requestIds: string[]) => {
    try {
      const approved = (await approveLinkChanges.mutateAsync(requestIds) as {
        approved: number;
      }).approved;
      setReviewing(false);
      // Nothing approved is not a success: every row may have been skipped as
      // the reader's own proposal, or resolved by someone else meanwhile.
      // Django says "Aucun changement à approuver." here; so do we.
      if (approved === 0) {
        toast.info(t("financialAids.linkChange.noneToApprove"));
      } else {
        toast.success(t("financialAids.linkChange.approvedCount", { count: approved }));
      }
    } catch {
      toast.error(t("financialAids.error"));
    }
  };

  // The import writes link sets directly and so ends any review open on the
  // resources it touches. Say which — ending somebody else's review should not
  // be something an administrator only finds later in a history drawer.
  const reportCancelledReviews = (report: { reviews_cancelled?: string[] }) => {
    const titles = report.reviews_cancelled ?? [];
    if (titles.length > 0) {
      toast.warning(
        t("financialAids.admin.importCancelledReviews", {
          count: titles.length,
          titles: titles.join(", "),
        }),
      );
    }
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // The list arrives in the API's order (alphabetical by title) — the same
  // default the Django table opens on. Sorting is client-side: the endpoint is
  // unpaginated, so the whole set is already here.
  // The pending-validation filter. `awaiting_validation_pending` is the server's
  // answer — the very set Django's own switch filters on — rather than
  // something re-derived from the links here: a staged order is keyed on an
  // *action*, so `action_order_pending` is true for every resource linked to
  // it, moved or not, and the list would read wider than the back-office's.
  const rows = sortAids(
    (list.data ?? []).filter((aid) => !pendingOnly || aid.awaiting_validation_pending),
    sort,
    descending,
  );

  // The MRCs under their région, plus the flat list the chips read from. A
  // territory saved before the picker existed can name something the catalogue
  // doesn't know; it is added back under its own heading so editing the aid
  // neither hides it nor drops it on save — exactly what the Django form does.
  //
  // Until the catalogue has actually loaded, nothing is known to be off it: the
  // aid's own territories are listed on their own so they stay visible and
  // selected, rather than being relabelled "Hors catalogue" — which is what a
  // failed request used to make the picker claim about perfectly valid MRCs,
  // while offering nothing else to pick.
  const territoryRegions = territories.isSuccess ? (territories.data ?? []) : [];
  const known = new Set(territoryRegions.flatMap((group) => group.mrcs));
  const legacy = territories.isSuccess
    ? form.territories.filter((name) => !known.has(name))
    : [];
  const territoryGroups = territories.isSuccess
    ? [
        ...territoryRegions.map((group) => ({
          heading: group.region,
          options: group.mrcs.map((name) => ({ value: name, label: name })),
        })),
        ...(legacy.length
          ? [
              {
                heading: t("financialAids.admin.fields.territoriesUnknown"),
                options: legacy.map((name) => ({ value: name, label: name })),
              },
            ]
          : []),
      ]
    : [
        {
          heading: "",
          options: form.territories.map((name) => ({ value: name, label: name })),
        },
      ];
  const territoryOptions = territoryGroups.flatMap((group) => group.options);

  // Sectors grouped by category for the searchable switch list (parity with the
  // expert registration selector; uncategorized sectors last under "Autres").
  const uncategorized = t("expert.register.uncategorized");
  const sectorGroups = (() => {
    const map = new Map<string, { value: string; label: string }[]>();
    for (const s of sectors.data ?? []) {
      const heading = s.category?.name ?? uncategorized;
      const list = map.get(heading) ?? [];
      list.push({ value: s.id, label: s.name });
      map.set(heading, list);
    }
    return [...map.entries()]
      .sort(([a], [b]) =>
        a === uncategorized ? 1 : b === uncategorized ? -1 : a.localeCompare(b),
      )
      .map(([heading, options]) => ({ heading, options }));
  })();

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">{t("financialAids.admin.title")}</h1>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <span className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-semibold hover:bg-accent">
              {t("financialAids.admin.import")}
            </span>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onImport(file);
                e.target.value = "";
              }}
            />
          </label>
          <label className="cursor-pointer">
            <span className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-semibold hover:bg-accent">
              {t("financialAids.admin.importActions")}
            </span>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onImportActions(file);
                e.target.value = "";
              }}
            />
          </label>
          <Button onClick={startCreate}>{t("financialAids.admin.create")}</Button>
        </div>
      </header>

      {creating && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={submit} className="space-y-3">
              {/* Active is a record-level property — a header switch (Actif /
                  Inactif), mirroring the Django aid form. */}
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-foreground">
                  {editing
                    ? t("financialAids.admin.editAid")
                    : t("financialAids.admin.create")}
                </h2>
                <div className="ml-auto flex items-center gap-3">
                {editing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setHistoryItem({ id: editing.id, name: editing.title })
                    }
                  >
                    {t("financialAids.admin.history")}
                  </Button>
                )}
                <label className="flex cursor-pointer items-center gap-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(next) => set("is_active", next)}
                    label={t("financialAids.admin.fields.isActive")}
                  />
                  <span
                    className={cn(
                      "min-w-[3.5rem] text-sm font-semibold",
                      form.is_active ? "text-success" : "text-muted-foreground",
                    )}
                  >
                    {form.is_active
                      ? t("financialAids.admin.fields.isActive")
                      : t("financialAids.admin.fields.isInactive")}
                  </span>
                </label>
                </div>
              </div>
              <Field label={t("financialAids.admin.fields.title")}>
                <input
                  className="w-full rounded-md border border-border px-3 py-2"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  required
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("financialAids.admin.fields.organization")}>
                  <input
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.organization}
                    onChange={(e) => set("organization", e.target.value)}
                    required
                  />
                </Field>
                <Field label={t("financialAids.admin.fields.programNumber")}>
                  <input
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.program_number}
                    onChange={(e) => set("program_number", e.target.value)}
                  />
                </Field>
              </div>
              {/* A programme can offer several kinds of financing, so this is
                  the same searchable switch list (with a select-all) the other
                  multi-selects use — parity with the Django form. */}
              <SelectionField
                label={t("financialAids.admin.fields.fundingTypes")}
                options={FUNDING_TYPES.map((type) => ({
                  value: type,
                  label: t(`financialAids.types.${type}`),
                }))}
                selected={form.funding_types}
                onChange={(next) => set("funding_types", next as FundingType[])}
                selectAllLabel={t("financialAids.admin.selectAllFundingTypes")}
              />
              <Field label={t("financialAids.admin.fields.officialUrl")}>
                <input
                  type="url"
                  className="w-full rounded-md border border-border px-3 py-2"
                  value={form.official_url}
                  onChange={(e) => set("official_url", e.target.value)}
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("financialAids.admin.fields.amountMin")}>
                  <MoneyInput
                    value={form.amount_min}
                    onValueChange={(next) => set("amount_min", next)}
                  />
                </Field>
                <Field label={t("financialAids.admin.fields.amountMax")}>
                  <MoneyInput
                    value={form.amount_max}
                    onValueChange={(next) => set("amount_max", next)}
                  />
                </Field>
              </div>
              <Field label={t("financialAids.admin.fields.amountDescription")}>
                <input
                  className="w-full rounded-md border border-border px-3 py-2"
                  value={form.amount_description}
                  onChange={(e) => set("amount_description", e.target.value)}
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("financialAids.admin.fields.eligibilityStart")}>
                  <input
                    type="date"
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.eligibility_start}
                    onChange={(e) => set("eligibility_start", e.target.value)}
                  />
                </Field>
                <Field label={t("financialAids.admin.fields.eligibilityEnd")}>
                  <input
                    type="date"
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.eligibility_end}
                    onChange={(e) => set("eligibility_end", e.target.value)}
                  />
                </Field>
              </div>
              {/* Contact person — the same fields as an expert respondent. */}
              <fieldset className="space-y-3 rounded-md border border-border p-3">
                <legend className="px-1 text-sm font-semibold">
                  {t("financialAids.admin.contactPerson")}
                </legend>
                <p className="text-xs text-muted-foreground">
                  {t("financialAids.admin.contactPersonHint")}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t("financialAids.admin.fields.contactLastName")}>
                    <input
                      className="w-full rounded-md border border-border px-3 py-2"
                      value={form.contact_last_name}
                      onChange={(e) => set("contact_last_name", e.target.value)}
                    />
                  </Field>
                  <Field label={t("financialAids.admin.fields.contactFirstName")}>
                    <input
                      className="w-full rounded-md border border-border px-3 py-2"
                      value={form.contact_first_name}
                      onChange={(e) => set("contact_first_name", e.target.value)}
                    />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t("financialAids.admin.fields.contactFunction")}>
                    <input
                      className="w-full rounded-md border border-border px-3 py-2"
                      value={form.contact_function}
                      onChange={(e) => set("contact_function", e.target.value)}
                    />
                  </Field>
                  <Field label={t("financialAids.admin.fields.contactEmail")}>
                    <input
                      type="email"
                      className="w-full rounded-md border border-border px-3 py-2"
                      value={form.contact_email}
                      onChange={(e) => set("contact_email", e.target.value)}
                    />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label={t("financialAids.admin.fields.contactPhone")}>
                    <input
                      type="tel"
                      inputMode="tel"
                      // Same rule as the Django form, the DRF serializer and
                      // every phone an expert types: a téléphone professionnel
                      // reaches the enterprise as a `tel:` link on the aid's
                      // sheet, so "asdfasdfasd" must not save. Flagged here and
                      // refused by submit, which is what the server would do.
                      aria-invalid={phoneInvalid || undefined}
                      className={cn(
                        "w-full rounded-md border px-3 py-2",
                        phoneInvalid ? "border-destructive" : "border-border",
                      )}
                      value={form.contact_phone}
                      onChange={(e) => set("contact_phone", e.target.value)}
                    />
                    {phoneInvalid && (
                      <span className="text-xs font-normal text-destructive">
                        {t("financialAids.admin.invalidPhone")}
                      </span>
                    )}
                  </Field>
                  <Field label={t("financialAids.admin.fields.contactExtension")}>
                    <input
                      className="w-full rounded-md border border-border px-3 py-2"
                      maxLength={10}
                      value={form.contact_extension}
                      onChange={(e) => set("contact_extension", e.target.value)}
                    />
                  </Field>
                </div>
              </fieldset>
              <Field label={t("financialAids.admin.fields.summary")}>
                <textarea
                  className="w-full rounded-md border border-border px-3 py-2"
                  rows={3}
                  maxLength={700}
                  value={form.summary}
                  onChange={(e) => set("summary", e.target.value)}
                />
              </Field>
              {/* Relevance/targeting metadata — drives the directory's
                  geo/sector ranking. Parity with the Django form. */}
              {/* Picked from the MRC catalogue, never typed: a territory only
                  reaches an enterprise when it is spelled the way the province's
                  layer spells it, and "Domaine-du-Roy" typed by hand reads right
                  while matching nothing ("Le Domaine-du-Roy"). Grouped by région,
                  like the Django switch list. */}
              <SelectionField
                label={t("financialAids.admin.fields.territories")}
                hint={t("financialAids.admin.fields.territoriesHint")}
                options={territoryOptions}
                groups={territoryGroups}
                selected={form.territories}
                onChange={(next) => set("territories", next)}
                selectAllLabel={t("financialAids.admin.selectAllTerritories")}
              />
              {/* Say so rather than presenting an empty catalogue as the truth. */}
              {territories.isPending && (
                <p className="text-xs text-muted-foreground">
                  {t("financialAids.loading")}
                </p>
              )}
              {territories.isError && (
                <p className="text-xs text-destructive">
                  {t("financialAids.admin.territoriesUnavailable")}{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => void territories.refetch()}
                  >
                    {t("financialAids.retry")}
                  </button>
                </p>
              )}
              <SelectionField
                label={t("financialAids.admin.fields.serviceRegions")}
                options={(regions.data ?? []).map((r) => ({
                  value: r.id,
                  label: r.name,
                }))}
                selected={form.service_regions}
                onChange={(next) => set("service_regions", next)}
                selectAllLabel={t("expert.register.selectAllRegions")}
                otherToggleLabel={t("financialAids.admin.otherToggle")}
                otherLabel={t("financialAids.admin.fields.serviceRegionsOther")}
                otherValue={form.service_regions_other}
                onOtherChange={(next) => set("service_regions_other", next)}
              />
              <SelectionField
                label={t("financialAids.admin.fields.sectorsServed")}
                options={(sectors.data ?? []).map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                groups={sectorGroups}
                selected={form.sectors_served}
                onChange={(next) => set("sectors_served", next)}
                selectAllLabel={t("expert.register.selectAllSectors")}
                otherToggleLabel={t("financialAids.admin.otherToggle")}
                otherLabel={t("financialAids.admin.fields.sectorsServedOther")}
                otherValue={form.sectors_served_other}
                onOtherChange={(next) => set("sectors_served_other", next)}
              />
              <SelectionField
                label={t("financialAids.admin.fields.companySizes")}
                options={(companySizes.data ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                selected={form.company_sizes_served}
                onChange={(next) => set("company_sizes_served", next)}
                selectAllLabel={t("expert.register.selectAllSizes")}
              />
              <SelectionField
                label={t("financialAids.admin.fields.organizationTypes")}
                options={ORG_TYPES.map((key) => ({
                  value: key,
                  label: t(`financialAids.orgTypes.${key}`),
                }))}
                selected={form.organization_types_served}
                onChange={(next) => set("organization_types_served", next)}
                selectAllLabel={t("expert.register.selectAllTypes")}
                otherToggleLabel={t("financialAids.admin.otherToggle")}
                otherLabel={t("financialAids.admin.fields.organizationTypesOther")}
                otherValue={form.organization_types_served_other}
                onOtherChange={(next) => set("organization_types_served_other", next)}
              />

              <div className="space-y-1 text-sm">
                <span className="font-medium">
                  {t("financialAids.admin.fields.actions")}
                </span>
                {pending && (
                  <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
                    <p className="text-sm text-foreground">
                      {t(
                        pending.is_mine
                          ? "financialAids.linkChange.formPendingMine"
                          : "financialAids.linkChange.formPendingReview",
                      )}
                    </p>
                    <LinkChangeByline pending={pending} ns="financialAids" />
                    <LinkChangeDiff pending={pending} ns="financialAids" />
                    <LinkChangeNote note={pending.note} ns="financialAids" />
                  </div>
                )}
                <ActionPicker
                  // While a change is staged the picker shows the *proposed*
                  // set, locked — except to a reviewer drafting a counter.
                  value={
                    countering
                      ? counterActions
                      : pending
                        ? proposedActions
                        : form.actions
                  }
                  locked={Boolean(pending) && !countering}
                  newIds={countering ? undefined : proposedNewIds}
                  onChange={(actions) =>
                    countering
                      ? setCounterActions(actions)
                      : set("actions", actions)
                  }
                  rowAction={(action) => {
                    const order = orderByAction.get(action.id);
                    if (!order) return null;
                    return (
                      <span className="flex items-center gap-1.5">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          // Outlined in ink, not the pale default: it sits next
                          // to a muted chip and a quiet remove icon and has to
                          // read as the row's real action (matches Django).
                          className="border-foreground text-foreground"
                          onClick={() =>
                            setOrderAction({ id: action.id, name: action.name })
                          }
                        >
                          {t("financialAids.linkOrder.open", { count: order.count })}
                        </Button>
                        {(order.pendingReview || order.pendingMine) && (
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                              order.pendingReview
                                ? "bg-warning/20 text-foreground"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            {t(
                              order.pendingReview
                                ? "financialAids.linkOrder.toValidate"
                                : "financialAids.linkOrder.pending",
                            )}
                          </span>
                        )}
                      </span>
                    );
                  }}
                />
              </div>
              {countering && (
                <LinkChangeNoteField
                  ns="financialAids"
                  note={counterNote}
                  onNote={setCounterNote}
                />
              )}
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={cancel}>
                  {t("financialAids.admin.cancel")}
                </Button>
                {pending && (
                  <LinkChangeDecisionControls
                    pending={pending}
                    ns="financialAids"
                    countering={countering}
                    note={counterNote}
                    onStartCounter={startCounter}
                    onCancelCounter={() => setCountering(false)}
                    busy={decideLinkChange.isPending}
                    onApprove={() =>
                      void decide({
                        decision: "approve",
                        request_id: pending.id,
                      })
                    }
                    onCounter={() =>
                      void decide({
                        decision: "counter",
                        request_id: pending.id,
                        ids: counterActions.map((a) => a.id),
                        note: counterNote,
                      })
                    }
                  />
                )}
                {/* Saving stays available while a change is staged: only the
                    links are frozen, the resource's own fields are not. */}
                {!countering && (
                  <Button type="submit">
                    {t(pending ? "financialAids.linkChange.saveOthers" : "financialAids.admin.save")}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <LinkChangeBanner
        count={(linkChanges.data ?? []).length}
        ns="financialAids"
        onOpen={() => setReviewing(true)}
      />

      {/* How many aides financières the list holds, as on the Django list. The
          response is unpaginated here, so its length *is* the total. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {list.data && list.data.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("financialAids.admin.countTotal", { count: rows.length })}
          </p>
        ) : (
          <span />
        )}
        {/* The Django list's "En attente de validation" switch. A staged
            *display order* counts too — either way the row awaits somebody. */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch
            checked={pendingOnly}
            onCheckedChange={setPendingOnly}
            label={t("financialAids.linkChange.pendingOnly")}
          />
          <span className="font-medium">
            {t("financialAids.linkChange.pendingOnly")}
          </span>
        </label>
      </div>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorState onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t(
            pendingOnly
              ? "financialAids.linkChange.noneAwaiting"
              : "financialAids.admin.empty",
          )}
        />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-end gap-2">
            <label
              htmlFor="fa-admin-sort"
              className="text-sm font-medium text-muted-foreground"
            >
              {t("financialAids.admin.sort.label")}
            </label>
            <select
              id="fa-admin-sort"
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
            >
              {SORT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`financialAids.admin.sort.${key}`)}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={sort === "default"}
              aria-pressed={descending}
              onClick={() => setDescending((prev) => !prev)}
            >
              {t(
                descending
                  ? "financialAids.admin.sort.descending"
                  : "financialAids.admin.sort.ascending",
              )}
            </Button>
          </div>
          {rows.map((aid) => (
            <Card key={aid.id}>
              <CardContent className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold">{aid.title}</p>
                    <StatusPill status={aid.status} />
                    {/* What this row awaits validation for — the same chips the
                        Django list shows beside the title. */}
                    <PendingValidationChips
                      namespace="financialAids"
                      status={aid.status}
                      linkChangePending={aid.link_change_pending}
                      linkChangePendingMine={aid.link_change_pending_mine}
                      orderChangePending={aid.order_change_pending}
                      orderChangePendingMine={aid.order_change_pending_mine}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {aid.funding_types_display.join(", ")} · {aid.organization} ·{" "}
                    {/* The eligibility window the Django table shows as its own
                        sortable column — needed here for the sort to be legible. */}
                    {formatEligibility(aid, t)} ·{" "}
                    {t("financialAids.admin.actionCount", {
                      count: aid.action_link_count,
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(aid)}>
                    {t("financialAids.admin.edit")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void remove(aid)}
                  >
                    {t("financialAids.admin.delete")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {historyItem && (
        <HistoryDrawer
          title={historyItem.name}
          onClose={() => setHistoryItem(null)}
          query={history}
        />
      )}
      {reviewing && (
        <LinkChangeReviewModal
          rows={linkChanges.data ?? []}
          ns="financialAids"
          isPending={approveLinkChanges.isPending}
          onApproveAll={(ids) => void approveAll(ids)}
          onOpenResource={(id) => {
            const aid = (list.data ?? []).find((item) => item.id === id);
            if (aid) startEdit(aid);
            setReviewing(false);
          }}
          onClose={() => setReviewing(false)}
        />
      )}
      {orderAction && (
        <LinkOrderDialog
          action={orderAction}
          ns="financialAids"
          useOrder={useActionFinancialAidOrder}
          useDecision={useActionFinancialAidOrderDecision}
          onClose={() => setOrderAction(null)}
        />
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
